import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { MetricDefinitionRecord } from "../MetricRecord";

import { metricsQueryKeys } from "./metricsQueryKeys";

/**
 * Single-page list of metric definitions.
 *
 * Use this when you want one page of results plus the `next_token`. For
 * infinite-scroll consumption, prefer `useInfiniteMetricDefinitions`.
 */
export function useMetricDefinitions(
  orgId: string | undefined,
  pageSize?: number,
) {
  const { metricsService } = useDomainServices();

  return useQuery<PaginatedResponse<MetricDefinitionRecord>, Error>({
    queryKey: metricsQueryKeys.definitionsList(orgId, pageSize),
    queryFn: ({ signal }) =>
      metricsService.listMetricDefinitions({
        abortSignal: signal,
        orgId,
        pageSize,
      }),
    enabled: orgId !== undefined,
    staleTime: 60_000,
  });
}

/**
 * Infinite-query variant that auto-follows `next_token`. Returns all pages
 * flattened on `data.pages.flatMap(...)`.
 */
export function useInfiniteMetricDefinitions(
  orgId: string | undefined,
  pageSize?: number,
) {
  const { metricsService } = useDomainServices();

  return useInfiniteQuery<PaginatedResponse<MetricDefinitionRecord>, Error>({
    queryKey: metricsQueryKeys.definitionsInfinite(orgId, pageSize),
    queryFn: ({ signal, pageParam }) =>
      metricsService.listMetricDefinitions({
        abortSignal: signal,
        orgId,
        pageSize,
        pageToken: pageParam as string | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_token ?? undefined,
    enabled: orgId !== undefined,
    staleTime: 60_000,
  });
}

/**
 * Convenience: returns ALL metric definitions as a flat array, draining
 * pagination internally. Use for small bounded sets (a few hundred); for
 * unbounded data, use `useInfiniteMetricDefinitions`.
 */
export function useAllMetricDefinitions(orgId: string | undefined) {
  const { metricsService } = useDomainServices();

  return useQuery<MetricDefinitionRecord[], Error>({
    queryKey: metricsQueryKeys.definitionsAll(orgId),
    queryFn: ({ signal }) =>
      metricsService.getAllMetricDefinitions({
        abortSignal: signal,
        orgId,
      }),
    enabled: orgId !== undefined,
    staleTime: 60_000,
  });
}
